export enum FilteredItems {
  vehicles = 'vehicles',
  customers = 'customers',
  agreements = 'agreements',
}

/** @deprecated Use FilteredItems enum instead */
export const FILTERED_ITEMS = {
  vehicles: FilteredItems.vehicles,
  customers: FilteredItems.customers,
  agreements: FilteredItems.agreements,
} as const;

export enum FilterType {
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  date = 'date',
  dateTime = 'dateTime',
  humanizedDate = 'humanizedDate',
  dropdown = 'dropdown',
  fuelDropdown = 'fuelDropdown',
  connectivity = 'connectivity',
  money = 'money',
  phone = 'phone',
}

/** @deprecated Use FilterType enum instead */
export const FILTER_TYPES = {
  string: FilterType.string,
  number: FilterType.number,
  boolean: FilterType.boolean,
  date: FilterType.date,
  dateTime: FilterType.dateTime,
  humanizedDate: FilterType.humanizedDate,
  dropdown: FilterType.dropdown,
  fuelDropdown: FilterType.fuelDropdown,
  connectivity: FilterType.connectivity,
  money: FilterType.money,
  phone: FilterType.phone,
} as const;

export enum FilterInputType {
  INPUT = 'input',
  RANGE = 'range',
  NULL = null,
}

export enum Comparator {
  isNull = 'isnull',
  exact = 'exact',
  contains = 'icontains',
  greaterThanOrEqual = 'gte',
  lessThanOrEqual = 'lte',
  greaterThan = 'gt',
  lessThan = 'lt',
  between = 'range',
  date = 'date',
}

const INVERTION_CHAR = '!';

interface FilterComparator {
  comparator: Comparator;
  inverted: boolean;
}

type ComparatorString = string & { __brand: 'ValidComparator' };

function isFilterComparator(comparator: string): comparator is Comparator {
  return Object.values(Comparator).includes(comparator as Comparator);
}

function parseComparatorBase(comparatorString: string): FilterComparator {
  let comparator = comparatorString;
  let inverted = false;

  if (comparatorString.startsWith(INVERTION_CHAR)) {
    comparator = comparatorString.slice(1);
    inverted = true;
  }

  if (!isFilterComparator(comparator)) {
    throw new Error(`Unknown filter comparator: ${comparatorString}`);
  }

  return { comparator, inverted };
}

function validateComparatatorString(comparatorString: string): comparatorString is ComparatorString {
  parseComparatorBase(comparatorString);
  return true;
}

function parseFilterComparator(comparatorString: string): FilterComparator {
  return parseComparatorBase(comparatorString);
}

function serializeFilterComparator({ comparator, inverted }: FilterComparator): ComparatorString {
  return (inverted ? `${INVERTION_CHAR}${comparator}` : comparator) as ComparatorString;
}

const stringFilterConf = [
  {
    translation: 'is',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'contains',
    comparator: serializeFilterComparator({ comparator: Comparator.contains, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isKnown',
    comparator: serializeFilterComparator({ comparator: Comparator.isNull, inverted: false }),
    type: null,
  },
  {
    translation: 'isUnknown',
    comparator: serializeFilterComparator({ comparator: Comparator.isNull, inverted: false }),
    type: null,
  },
];

const numberFilterConf = [
  {
    translation: 'isEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isNotEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: true }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isGreater',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isGreaterOrEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isLess',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isLessOrEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isKnown',
    comparator: serializeFilterComparator({ comparator: Comparator.isNull, inverted: false }),
    type: null,
  },
  {
    translation: 'isUnknown',
    comparator: serializeFilterComparator({ comparator: Comparator.isNull, inverted: false }),
    type: null,
  },
];

const booleanFilterConf = [
  {
    translation: 'isTrue',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
  },
  {
    translation: 'isFalse',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
  },
];

const dateFilterConf = [
  {
    translation: 'is',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isBefore',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isBeforeIncluding',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isAfter',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isAfterIncluding',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'between',
    comparator: serializeFilterComparator({ comparator: Comparator.between, inverted: false }),
    type: FilterInputType.RANGE,
  },
  // TODO: Disabled until backend is ready
  // {
  //   translation: 'isKnown',
  //   comparator: FilterComparator.isNull,
  //   type: null,
  // },
  // {
  //   translation: 'isUnknown',
  //   comparator: FilterComparator.isNull,
  //   type: null,
  // },
];

const dateTimeFilterConf = [
  {
    translation: 'isBefore',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isBeforeIncluding',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isAfter',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThan, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'isAfterIncluding',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThanOrEqual, inverted: false }),
    type: FilterInputType.INPUT,
  },
  {
    translation: 'is',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
    disabled: true,
  },
  {
    translation: 'between',
    comparator: serializeFilterComparator({ comparator: Comparator.between, inverted: false }),
    type: FilterInputType.RANGE,
  },
  // TODO: Disabled until backend is ready
  // {
  //   translation: 'isKnown',
  //   comparator: FilterComparator.isNull,
  //   type: null,
  // },
  // {
  //   translation: 'isUnknown',
  //   comparator: FilterComparator.isNull,
  //   type: null,
  // },
];

const dropdownFilterConf = [
  {
    translation: 'is',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
  },
];

const dropdownFuelFilterConf = [
  {
    translation: 'isGreater',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThan, inverted: false }),
    type: null,
  },
  {
    translation: 'isGreaterOrEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.greaterThanOrEqual, inverted: false }),
    type: null,
  },
  {
    translation: 'isLess',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThan, inverted: false }),
    type: null,
  },
  {
    translation: 'isLessOrEqual',
    comparator: serializeFilterComparator({ comparator: Comparator.lessThanOrEqual, inverted: false }),
    type: null,
  },
];

const connectivityFilterConf = [
  {
    translation: 'isConnected',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
  },
  {
    translation: 'isDisconnected',
    comparator: serializeFilterComparator({ comparator: Comparator.exact, inverted: false }),
    type: null,
    // TODO: Disabled until backend is ready
    // }, {
    //     translation: 'connectivityIsUnknown',
    //     comparator: FilterComparator.exact,
    //     type: null,
  },
];

export const FILTER_FORM = {
  string: stringFilterConf,
  number: numberFilterConf,
  boolean: booleanFilterConf,
  date: dateFilterConf,
  dateTime: dateTimeFilterConf,
  humanizedDate: dateTimeFilterConf,
  dropdown: dropdownFilterConf,
  fuelDropdown: dropdownFuelFilterConf,
  connectivity: connectivityFilterConf,
  money: numberFilterConf,
  phone: stringFilterConf,
} as const;
